import { useEffect, useState } from 'react';
import { Check, ChevronDown, Copy, Moon, MinusCircle, CircleDot } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/OS/components/ui/button';
import { Input } from '@/OS/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/blueprints/ui/OSAwarePopover';
import { useToast } from '@/components/ui/use-toast';
import type { PresenceStatus } from '@/hooks/useUserMessages';
import { cn } from '@/lib/utils';

const STATUS_OPTIONS: Array<{
  value: Exclude<PresenceStatus, 'offline'>;
  label: string;
  description: string;
}> = [
  { value: 'online', label: 'Online', description: 'Available to chat now' },
  { value: 'away', label: 'Away', description: 'Temporarily away from keyboard' },
  { value: 'dnd', label: 'Do Not Disturb', description: 'Mute interruptions while you focus' },
];

const statusDotClassName: Record<PresenceStatus, string> = {
  online: 'bg-emerald-500',
  away: 'bg-amber-400',
  dnd: 'bg-red-500',
  offline: 'bg-slate-400',
};

const statusIconMap = {
  online: CircleDot,
  away: Moon,
  dnd: MinusCircle,
};

interface StatusMenuProps {
  currentUserName: string;
  currentUserAvatar?: string;
  messengerId?: string | null;
  messengerIdLoading?: boolean;
  status: Exclude<PresenceStatus, 'offline'>;
  statusMessage: string;
  onChange: (status: Exclude<PresenceStatus, 'offline'>, statusMessage: string) => Promise<void> | void;
}

export default function StatusMenu({
  currentUserName,
  currentUserAvatar,
  messengerId,
  messengerIdLoading = false,
  status,
  statusMessage,
  onChange,
}: StatusMenuProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<Exclude<PresenceStatus, 'offline'>>(status);
  const [draftMessage, setDraftMessage] = useState(statusMessage);
  const [idCopied, setIdCopied] = useState(false);

  useEffect(() => {
    setDraftStatus(status);
  }, [status]);

  useEffect(() => {
    setDraftMessage(statusMessage);
  }, [statusMessage]);

  const activeOption = STATUS_OPTIONS.find((option) => option.value === status) ?? STATUS_OPTIONS[0];
  const ActiveStatusIcon = statusIconMap[activeOption.value];

  const handleSave = async () => {
    await onChange(draftStatus, draftMessage.trim().slice(0, 80));
    setOpen(false);
  };

  const handleStatusSelect = async (nextStatus: Exclude<PresenceStatus, 'offline'>) => {
    setDraftStatus(nextStatus);
    await onChange(nextStatus, draftMessage.trim().slice(0, 80));
    setOpen(false);
  };

  const handleCopyId = async () => {
    if (!messengerId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(messengerId);
      setIdCopied(true);
      window.setTimeout(() => setIdCopied(false), 1400);
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Please copy the ID manually.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="rounded-xl border border-border/70 bg-card/80 p-3 shadow-sm">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <Avatar className="h-11 w-11 border border-border/60">
              <AvatarImage src={currentUserAvatar} alt={currentUserName} />
              <AvatarFallback>{currentUserName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <span
              className={cn(
                'absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-card',
                statusDotClassName[status]
              )}
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{currentUserName}</p>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {statusMessage || activeOption.description}
            </p>
          </div>

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
              >
                <ActiveStatusIcon className="h-4 w-4" />
                <span className="hidden sm:inline">{activeOption.label}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              embedded
              className="z-[1200000] w-80 space-y-3 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-lg"
            >
              <div>
                <h3 className="text-sm font-semibold text-foreground">Presence</h3>
                <p className="text-xs text-muted-foreground">Set how you appear to your connections.</p>
              </div>

              <div className="space-y-2">
                {STATUS_OPTIONS.map((option) => {
                  const OptionIcon = statusIconMap[option.value];
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={cn(
                        'flex w-full items-start gap-3 rounded-md border border-border bg-card p-3 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                        draftStatus === option.value && 'border-primary bg-accent text-accent-foreground shadow-sm'
                      )}
                      onClick={() => void handleStatusSelect(option.value)}
                    >
                      <span
                        className={cn(
                          'mt-0.5 h-2.5 w-2.5 rounded-full',
                          statusDotClassName[option.value]
                        )}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-current">
                          <OptionIcon className="h-4 w-4" />
                          {option.label}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {option.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                <label htmlFor="status-message" className="text-xs font-medium text-muted-foreground">
                  Status message
                </label>
                <Input
                  id="status-message"
                  value={draftMessage}
                  onChange={(event) => setDraftMessage(event.target.value.slice(0, 80))}
                  placeholder="Working on something important"
                  maxLength={80}
                />
                <p className="text-[11px] text-muted-foreground">Shown under your name while you're online.</p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave}>
                  Save
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="truncate font-mono tracking-[0.16em]">
            {messengerIdLoading ? 'Loading…' : messengerId ? `ID ${messengerId}` : 'ID unavailable'}
          </span>
          <button
            type="button"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleCopyId}
            disabled={!messengerId || messengerIdLoading}
            aria-label="Copy Messenger ID"
            title="Copy Messenger ID"
          >
            {idCopied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}